/**
 * Função única de modo de layout (§4.1, M27), retângulo do mapa (§4.3.2) e constantes de layout (§4.1.1, §4.3.1).
 * A MESMA função roda no esboço de boot (index.html, antes do JS do app) e no app: só `import type`, sem DOM, sem
 * dependências, sem recursos além do ES5 depois do rebaixamento do inlineBootPlugin (§12.7).
 * As constantes existem também como tokens --layout-* (tokens.css e modes.css); layout-constants.test.ts compara.
 */
import type { HeightClass, LayoutMode, LayoutState } from '../model/types';

export interface LayoutResult {
  mode: LayoutMode;
  /** flow, tablet ou land. */
  phone: boolean;
  heightClass: HeightClass;
  zoom: number;
  stageW: number;
  stageH: number;
  /** Palco lógico com largura ≥ 1280 fora do celular (§4.4.1). */
  wide: boolean;
}

export const LAYOUT = {
  PHONE_MAX_W: 1000,
  PHONE_MIN_LAND_H: 640,
  TABLET_MIN_W: 600,
  WIDE_MIN_W: 1280,
  STAGE_W: 1600,
  STAGE_H: 900,
  ZOOM_MIN: 1,
  ZOOM_MAX: 3,
  TV_STAGE_W: 1280,
  TV_STAGE_H: 720,
  TV_ZOOM_MIN: 0.8,
  TV_ZOOM_MAX: 4,
  H_TINY: 680,
  H_SHORT: 780,
  H_MID: 900,
  MARGIN: 20,
  HEADER_TOP: 14,
  HEADER_H: 44,
  TOP: 72,
  COL_LEFT: 336,
  COL_RIGHT: 308,
  TV_COL_LEFT: 360,
  TV_COL_RIGHT: 300,
  GUTTER: 20,
  MAPTOP_OFFSET: 44,
  TIMELINE_RESERVE: 62,
  TV_TICKER_RESERVE: 58,
  MAP_MIN: 80,
  MAP_MIN_PHONE: 40,
  PHONE_MARGIN: 16,
  MAPTOP_H: 32,
  TIMELINE_H: 48,
  TV_TICKER_H: 44,
  CHIP_RESERVE: 92,
  TV_CHIP_RESERVE: 112,
  PHONE_CHIP_RESERVE: 52,
  /** SimulationBanner (§6.9). */
  SIM_BANNER_H: 28,
  TV_SIM_BANNER_H: 36,
  /** PartnerSlot `bar` (§12.5, P2); só conta quando a barra está montada. */
  PARTNER_BAR_H: 32,
  PARTNER_BAR_H_COARSE: 44,
} as const;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * §4.1.1. `w = innerWidth`; `h = innerHeight − topBarsH`; `tv = ui.tv.on` (ignorado no celular);
 * `zoomSupported = CSS.supports('zoom', '2')` (sem suporte: zoom 1 e palco = janela, §4.2.1).
 */
export function layoutMode(w: number, h: number, tv: boolean, zoomSupported = true): LayoutResult {
  const L = LAYOUT;
  const portrait = h >= w;
  const phone = w < L.PHONE_MAX_W && (portrait || h < L.PHONE_MIN_LAND_H);
  const mode: LayoutMode = phone
    ? !portrait
      ? 'land'
      : w >= L.TABLET_MIN_W
        ? 'tablet'
        : 'flow'
    : tv
      ? 'tv'
      : 'desktop';
  let zoom = 1;
  if (zoomSupported && mode === 'desktop') zoom = clamp(Math.min(w / L.STAGE_W, h / L.STAGE_H), L.ZOOM_MIN, L.ZOOM_MAX);
  if (zoomSupported && mode === 'tv') {
    zoom = clamp(Math.min(w / L.TV_STAGE_W, h / L.TV_STAGE_H), L.TV_ZOOM_MIN, L.TV_ZOOM_MAX);
  }
  const stageW = w / zoom;
  const stageH = h / zoom;
  const heightClass: HeightClass =
    mode === 'tv'
      ? 'mid'
      : mode === 'flow'
        ? 'tall'
        : phone
          ? 'short'
          : stageH < L.H_TINY
            ? 'tiny'
            : stageH < L.H_SHORT
              ? 'short'
              : stageH < L.H_MID
                ? 'mid'
                : 'tall';
  return { mode, phone, heightClass, zoom, stageW, stageH, wide: !phone && stageW >= L.WIDE_MIN_W };
}

/** Faixas fixas acima do app (§4.1 "Entradas"): SimulationBanner e PartnerSlot `bar` (P2). */
export interface TopBars {
  /** SimulationBanner visível (no boot: `config.mode === 'simulation'`). */
  simulation: boolean;
  /** PartnerSlot `bar` montado (P2, §12.5). */
  partnerBar?: boolean;
  /** `(pointer: coarse)`: a barra de parceiro tem 44 px. */
  coarse?: boolean;
}

/** Soma das alturas das faixas do topo; a SimulationBanner tem 36 px na TV (§6.9). */
export function topBarsHeight(bars: TopBars, tvMode: boolean): number {
  const L = LAYOUT;
  let h = 0;
  if (bars.simulation) h += tvMode ? L.TV_SIM_BANNER_H : L.SIM_BANNER_H;
  if (bars.partnerBar) h += bars.coarse ? L.PARTNER_BAR_H_COARSE : L.PARTNER_BAR_H;
  return h;
}

export interface ViewportInput extends TopBars {
  /** ui.tv.on (ou `tv=1` aceito na montagem). */
  tv: boolean;
  zoomSupported?: boolean;
}

/**
 * Atalho do esboço de boot e do useLayoutMode: desconta as faixas do topo da janela e decide o modo (§4.1, AC-4.1-05).
 * A faixa da simulação mede 36 px só quando o modo final é `tv` (no celular a TV é ignorada).
 */
export function layoutForViewport(
  innerW: number,
  innerH: number,
  input: ViewportInput,
): { result: LayoutResult; topBarsH: number } {
  const zs = input.zoomSupported !== false;
  let topBarsH = topBarsHeight(input, input.tv);
  let result = layoutMode(innerW, innerH - topBarsH, input.tv, zs);
  if (input.tv && result.mode !== 'tv') {
    topBarsH = topBarsHeight(input, false);
    result = layoutMode(innerW, innerH - topBarsH, false, zs);
  }
  return { result, topBarsH };
}

/** Valor do sinal `layout` (§2.4.1) a partir do resultado e de `(pointer: coarse)`. */
export function toLayoutState(r: LayoutResult, pointerCoarse: boolean): LayoutState {
  return {
    mode: r.mode,
    heightClass: r.heightClass,
    zoom: r.zoom,
    stageW: r.stageW,
    stageH: r.stageH,
    pointerCoarse,
  };
}

/** O sinal só é escrito se algum campo mudou (§4.1). */
export function sameLayout(a: LayoutState, b: LayoutState): boolean {
  return (
    a.mode === b.mode &&
    a.heightClass === b.heightClass &&
    a.zoom === b.zoom &&
    a.stageW === b.stageW &&
    a.stageH === b.stageH &&
    a.pointerCoarse === b.pointerCoarse
  );
}

/** flow, tablet e land. */
export function isPhoneMode(mode: LayoutMode): boolean {
  return mode === 'flow' || mode === 'tablet' || mode === 'land';
}

/** `wide` de §4.4.1 sobre o sinal `layout`. */
export function isWide(s: Pick<LayoutState, 'mode' | 'stageW'>): boolean {
  return !isPhoneMode(s.mode) && s.stageW >= LAYOUT.WIDE_MIN_W;
}

/**
 * Atributos do <html> (§4.1 "Saídas"): data-mode, data-h, data-phone (presente em flow, tablet e land; null = remover),
 * data-pointer e a classe `flow`. Esboço e app aplicam o mesmo objeto.
 */
export function layoutAttributes(s: LayoutState): {
  'data-mode': LayoutMode;
  'data-h': HeightClass;
  'data-phone': '' | null;
  'data-pointer': 'coarse' | 'fine';
  flowClass: boolean;
} {
  return {
    'data-mode': s.mode,
    'data-h': s.heightClass,
    'data-phone': isPhoneMode(s.mode) ? '' : null,
    'data-pointer': s.pointerCoarse ? 'coarse' : 'fine',
    flowClass: s.mode === 'flow',
  };
}

/** §4.3.2. O canvas cobre o palco inteiro; o retângulo é só a área de enquadramento (§9.8). */
export function mapRect(
  stageW: number,
  stageH: number,
  cols: { left: boolean; right: boolean },
  tv: boolean,
): { x0: number; x1: number; y0: number; y1: number; ok: boolean } {
  const L = LAYOUT;
  const cl = tv ? L.TV_COL_LEFT : L.COL_LEFT;
  const cr = tv ? L.TV_COL_RIGHT : L.COL_RIGHT;
  const x0 = cols.left ? L.MARGIN + cl + L.GUTTER : L.MARGIN; // 376 (TV 400) ou 20
  const x1 = cols.right ? stageW - L.MARGIN - (cr + L.GUTTER) : stageW - L.MARGIN;
  const y0 = L.TOP + L.MAPTOP_OFFSET; // 116
  const y1 = stageH - L.MARGIN - (tv ? L.TV_TICKER_RESERVE : L.TIMELINE_RESERVE); // H − 82 (TV H − 78)
  return { x0, x1, y0, y1, ok: x1 - x0 > L.MAP_MIN && y1 - y0 > L.MAP_MIN };
}
